import { createContext, forwardRef, useContext } from 'react'
import { useControllableValue } from '@/ui/kit'
import { cn } from './internal/utils'
import { FOCUS_RING } from './internal/presets'

const GroupContext = createContext(null)

/**
 * antd Checkbox. Reports an event whose `target.checked` is set, which is what
 * the codebase reads. Participates in `Checkbox.Group` when nested in one.
 */
const Checkbox = forwardRef(
  (
    {
      checked,
      defaultChecked,
      onChange,
      indeterminate = false,
      disabled = false,
      value,
      className,
      style,
      children,
      ...rest
    },
    ref
  ) => {
    const group = useContext(GroupContext)
    const [innerChecked, setInnerChecked] = useControllableValue(checked, defaultChecked ?? false)

    const isGrouped = Boolean(group) && value !== undefined
    const isChecked = isGrouped ? group.value.includes(value) : innerChecked
    const isDisabled = disabled || group?.disabled

    const handleChange = (event) => {
      if (isGrouped) {
        group.toggle(value, event.target.checked)
        onChange?.(event)
        return
      }
      setInnerChecked(event.target.checked)
      onChange?.(event)
    }

    return (
      <label
        className={cn(
          'ui-checkbox-wrapper inline-flex cursor-pointer items-center gap-2 text-sm',
          isDisabled && 'ui-checkbox-wrapper-disabled cursor-not-allowed opacity-60',
          className
        )}
        style={style}
      >
        <span className="relative inline-flex h-4 w-4 shrink-0 items-center justify-center">
          <input
            ref={ref}
            type="checkbox"
            checked={isChecked}
            disabled={isDisabled}
            value={value}
            aria-checked={indeterminate ? 'mixed' : isChecked}
            onChange={handleChange}
            className={cn(
              'ui-checkbox-input peer h-4 w-4 cursor-pointer appearance-none rounded-[3px] border border-[var(--border-strong)] bg-[var(--surface-card)] checked:border-[var(--primary-color)] checked:bg-[var(--primary-color)] disabled:cursor-not-allowed',
              FOCUS_RING
            )}
            {...rest}
          />
          {indeterminate && !isChecked ? (
            <span className="ui-checkbox-inner-indeterminate pointer-events-none absolute h-0.5 w-2 rounded bg-[var(--primary-color)]" />
          ) : (
            <svg
              viewBox="0 0 16 16"
              aria-hidden="true"
              className="pointer-events-none absolute h-3 w-3 text-white opacity-0 peer-checked:opacity-100"
            >
              <path
                d="M3 8.5l3 3 7-7"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
              />
            </svg>
          )}
        </span>
        {children != null ? <span>{children}</span> : null}
      </label>
    )
  }
)
Checkbox.displayName = 'Checkbox'

const Group = ({
  value,
  defaultValue,
  onChange,
  options,
  disabled = false,
  className,
  style,
  children,
  ...rest
}) => {
  const [innerValue, setInnerValue] = useControllableValue(value, defaultValue ?? [])

  const toggle = (optionValue, nextChecked) => {
    const next = nextChecked
      ? [...innerValue, optionValue]
      : innerValue.filter((item) => item !== optionValue)
    setInnerValue(next)
    onChange?.(next)
  }

  const context = { value: innerValue ?? [], toggle, disabled }

  return (
    <GroupContext.Provider value={context}>
      <div className={cn('ui-checkbox-group flex flex-wrap items-center gap-3', className)} style={style} {...rest}>
        {options?.length
          ? options.map((option) => {
              const resolved = typeof option === 'object' ? option : { label: option, value: option }
              return (
                <Checkbox key={String(resolved.value)} value={resolved.value} disabled={resolved.disabled}>
                  {resolved.label}
                </Checkbox>
              )
            })
          : children}
      </div>
    </GroupContext.Provider>
  )
}

Checkbox.Group = Group

export default Checkbox
